import { useState, useEffect, useCallback } from 'react'

// Tracks Apple Pay / Google Pay availability detected via a hidden
// Express Checkout Element. The actual ECE mounts in DiscoveryECE,
// which calls this hook's handleReady when Stripe fires the onReady
// event with the available payment methods for the device.
//
// State shape:
//   applePay  — true when ECE reports Apple Pay is available
//   googlePay — true when ECE reports Google Pay is available
//   ready     — false while detection is in flight, true once either
//               (a) onReady has fired, or (b) the timeout has tripped
//
// The 5-second timeout is a Honeybadger-notified safety net plus the
// donor's patience floor for the hybrid block: under normal conditions
// onReady fires sub-500ms, so 5s is ~10× typical, generous enough to
// not false-positive on legitimately slow connections, short enough
// that the spinner doesn't feel like the page is stuck. When it trips
// we log it and flip ready to true with both wallets false so the
// picker degrades to non-wallet methods.
const DISCOVERY_TIMEOUT_MS = 5_000

const useExpressCheckoutDiscovery = ({ enabled = true } = {}) => {
  const [ availability, setAvailability ] = useState({
    applePay:  false,
    googlePay: false,
    ready:     false,
  })

  const handleReady = useCallback(({ availablePaymentMethods }) => {
    setAvailability({
      applePay:  !!availablePaymentMethods?.applePay,
      googlePay: !!availablePaymentMethods?.googlePay,
      ready:     true,
    })
  }, [])

  // When Stripe isn't configured at all (no publishable key), short-
  // circuit ready=true with no wallets so the picker doesn't sit on the
  // hybrid block waiting for an event that will never fire.
  useEffect(() => {
    if (!enabled) {
      setAvailability(s => s.ready ? s : { ...s, ready: true })
    }
  }, [ enabled ])

  useEffect(() => {
    if (!enabled || availability.ready) return
    const timer = setTimeout(() => {
      Honeybadger.notify('Stripe Express Checkout discovery timed out', {
        context: { timeoutMs: DISCOVERY_TIMEOUT_MS },
      })
      setAvailability(s => s.ready ? s : { ...s, ready: true })
    }, DISCOVERY_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [ enabled, availability.ready ])

  return { ...availability, handleReady }
}

export default useExpressCheckoutDiscovery
